import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import PmaxCsvModal from "../components/PmaxCsvModal";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, CircleDashed, AlertCircle } from "lucide-react";

const LABELS = {
  connect:  "Connecting to account",
  pull:     "Pulling campaign data",
  analyze:  "Analyzing performance",
  generate: "Generating briefing",
};

export default function JobProgressPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let attempt = 0;

    const tick = async () => {
      try {
        const j = await api.getJob(jobId);
        if (cancelled) return;
        setJob(j);
        attempt = 0;
        if (j.status === "complete" && j.briefing_id) {
          setTimeout(() => navigate(`/briefings/${j.briefing_id}`, { replace: true }), 600);
          return;
        }
        if (j.status === "failed") {
          setError(j.error || "Briefing failed.");
          return;
        }
        if (j.status === "awaiting_pmax_csv") {
          setCsvModalOpen(true);
        }
        const delay = Math.min(1500 * Math.pow(1.4, attempt), 5000);
        attempt++;
        setTimeout(tick, delay);
      } catch (e) {
        if (cancelled) return;
        attempt++;
        if (attempt >= 5) {
          setError(e?.response?.data?.detail || "Couldn't load job status.");
          return;
        }
        setTimeout(tick, Math.min(2000 * attempt, 10000));
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [jobId, navigate]);

  const stages = job?.stages || [];
  const progress = job?.progress || 0;

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-16 md:px-10 md:py-24">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
          {job?.status === "complete" ? "done" : "in progress"}
        </p>
        <h1 className="mt-2 font-heading text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl">
          {job?.status === "complete"
            ? "Your briefing is ready."
            : job?.status === "failed"
              ? "Something went wrong."
              : job?.status === "awaiting_pmax_csv"
                ? "Almost there — optional PMAX enrichment."
                : "Reading the account."}
        </h1>
        <p className="mt-3 text-base text-slate-600">
          {job?.status === "complete"
            ? "Redirecting to the briefing…"
            : job?.status === "awaiting_pmax_csv"
              ? "We detected PMAX campaigns. Add a CSV of older search terms, or skip."
              : "Usually takes 2–4 minutes for accounts under 200 campaigns."}
        </p>

        <div className="card-soft mt-10 p-6">
          <div className="flex items-center justify-between">
            <p className="font-heading text-sm font-medium text-slate-900">Progress</p>
            <p className="num text-sm font-semibold text-slate-700">{progress}%</p>
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-full bg-[#0052FF] transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="progress-bar"
            />
          </div>

          <ul className="mt-8 space-y-4">
            {stages.map((s) => (
              <li
                key={s.name}
                className="flex items-center gap-3"
                data-testid={`stage-${s.name}-${s.status}`}
              >
                <StageIcon status={s.status} />
                <span
                  className={`text-sm ${
                    s.status === "done"
                      ? "text-slate-500 line-through"
                      : s.status === "active"
                        ? "font-medium text-slate-900"
                        : "text-slate-400"
                  }`}
                >
                  {LABELS[s.name] || s.name}
                  {s.status === "active" && (
                    <span className="ml-2 inline-flex text-slate-400">
                      <span className="loading-dot">·</span>
                      <span className="loading-dot">·</span>
                      <span className="loading-dot">·</span>
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          {error && (
            <div
              className="mt-6 rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
              data-testid="job-error"
            >
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Briefing failed</p>
                  <p className="mt-1 text-xs">{error}</p>
                </div>
              </div>
              <button
                onClick={() => navigate("/new")}
                className="mt-3 inline-flex items-center rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100"
                data-testid="retry-btn"
              >
                Try again
              </button>
            </div>
          )}
        </div>

      </div>

      {csvModalOpen && job?.status === "awaiting_pmax_csv" && (
        <PmaxCsvModal
          jobId={jobId}
          pmaxContext={job.pmax_context}
          onDone={() => setCsvModalOpen(false)}
        />
      )}
    </Layout>
  );
}

function StageIcon({ status }) {
  if (status === "done") return <CheckCircle2 size={18} strokeWidth={1.8} className="text-emerald-600" />;
  if (status === "active") return <Loader2 size={18} strokeWidth={1.8} className="animate-spin text-[#0052FF]" />;
  if (status === "failed") return <AlertCircle size={18} strokeWidth={1.8} className="text-rose-600" />;
  return <CircleDashed size={18} strokeWidth={1.8} className="text-slate-300" />;
}
